// services/apiService.js
import axios from 'axios';
import Notify from 'quasar/src/plugins/notify/Notify.js';;
import AuthService from './authService';
import store from '@/store';

const API = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || 'http://localhost:5005/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

API.interceptors.request.use(
  (config) => {
    const token = AuthService.getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    store.dispatch('loading/startLoading');
    return config;
  },
  (error) => Promise.reject(error)
);

API.interceptors.response.use(
  (response) => {
    store.dispatch('loading/stopLoading');
    return response;
  },
  (error) => {
    store.dispatch('loading/stopLoading');
    if (error.response && error.response.status === 401) {
      error.customMessage =
        'Unauthorized access. Please contact the administrator.';
    } else if (error.response && error.response.status === 409) {
      error.customMessage = error.response.data.message;
    } else if (!error.response) {
      error.customMessage =
        'No response received from the server. Please check your network connection and try again.';
    }
    return Promise.reject(error);
  }
);

const handleResponse = async (request) => {
  try {
    const response = await request();
    if (response.data) {
      return response.data;
    } else {
      throw new Error('Unexpected response format');
    }
  } catch (error) {
    if (error.response && error.response.status === 401) {
      // Handle unauthorized error
      window.EventBus.$emit('showUnauthorizedError');
    }
    // Re-throw the error so that the component can handle it
    throw error;
  }
};

export const fetchRoles = async () => handleResponse(() => API.get('/roles'));
export const fetchParentData = async (parentId) =>
  handleResponse(() => API.get(`/activities/parent/${parentId}/home-data`));

export const fetchActivities = async (parentId) =>
  handleResponse(() => API.get(`activities/parent/${parentId}/activities`));

export const fetchChildren = async (parentId) =>
  handleResponse(() => API.get(`users/${parentId}/children`));

export const excuseChild = async (
  sessionId,
  childId,
  excused,
  comment = ''
) => {
  return handleResponse(() =>
    API.post(`/attendance/excuse/${sessionId}/${childId}`, { excused, comment })
  );
};

export const rescindExcuse = async (requestId) => {
  return handleResponse(() =>
    API.post(`/attendance/rescindExcuse/${requestId}`)
  );
};

export const markAttendance = async (sessionId, childId, status) => {
  return handleResponse(() =>
    API.post(`/attendance/attendance/${sessionId}/${childId}`, { status })
  );
};

export const excuseSession = async (sessionInfo) => {
  const { sessionId, childId } = sessionInfo;
  return handleResponse(() =>
    API.post(`/attendance/excuse/${sessionId}/${childId}`, { excused: true })
  );
};

export const fetchInfoRequestsForParent = async (parentId) => {
  try {
    const response = await API.get(`/attendance/info-requests/parent/${parentId}`);
    return response.data.requests || [];
  } catch (error) {
    console.error('Error fetching info requests:', error);
    throw error;
  }
};

export const provideAdditionalInfo = async (requestId, formData) => {
  try {
    // Create a new axios request without default headers
    const response = await axios.post(
      `/attendance/absence-approvals/${requestId}/provide-info`,
      formData,
      {
        // Don't set Content-Type for FormData - let browser handle it
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
          // No Content-Type header
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error providing additional info:', error);
    throw error;
  }
};

export const bookSlot = async (bookingInfo) =>
  handleResponse(() => API.post('/booking/bookSlot', bookingInfo));

export const toggleAttendanceStatus = async (childId, sessionId, status) =>
  handleResponse(() => API.post(`/attendance/${sessionId}/${childId}`, status));

export const fetchPendingBookings = async () =>
  handleResponse(() => API.get('/booking/pending'));

export const bookActivity = async (bookingInfo) =>
  handleResponse(() => API.post('/booking/bookActivity', bookingInfo));

export const cancelBooking = async (bookingInfo) => {
  console.log('API Call to cancel booking with payload:', bookingInfo);

  

  try {
    const response = await axios.post(
      '/booking/cancelbooking',
      bookingInfo,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    console.log('API Response:', response.data);
    return response.data;
  } catch (error) {
    console.error('API Error during booking cancellation:', error);
    throw error;
  }
};

export const fetchAttendanceRequests = async () =>
  handleResponse(() => API.get('/attendance/excuseRequests/pending'));

export const fetchBookingsOverview = async () => {
  const response = await axios.get('/booking/all-bookings-by-parent');
  return response.data;
};

export const approveBooking = async (bookingId) =>
  handleResponse(() => API.post(`/booking/approveBooking/${bookingId}`));

export const rejectBooking = async (bookingId) =>
  handleResponse(() => API.post(`/booking/rejectBooking/${bookingId}`));

export const fetchUpcomingSessions = async (parentId) =>
  handleResponse(() =>
    API.get(`sessions/parent/${parentId}/upcoming-sessions`)
  );

export const fetchActivityDetails = async (activityId, childId) => {
  try {
    const [activityResponse, sessionsResponse] = await Promise.all([
      API.get(`/activities/${activityId}`),
      API.get(`/sessions/${activityId}`),
    ]);

    const activity = activityResponse.data;

    activity.sessions = sessionsResponse.data.map((session) => {
      const participant = session.participants.find(
        (p) => p.participantId === childId
      );

      // Log session note details
      if (session.note && session.note.content) {
        console.log(
          `Session ID: ${session.sessionId} has a note:`,
          session.note.content
        );
      } else {
        console.log(`Session ID: ${session.sessionId} has no note.`);
      }

      return {
        ...session,
        status: session.status, // Use session-level status
        excused: participant ? participant.excused : false, // Excused status
        note: session.note, // Ensure the note object is included
      };
    });

    return activity;
  } catch (error) {
    console.error('Error fetching activity details:', error);
    throw new Error('Error fetching activity details');
  }
};

export const fetchParentReminders = async (parentId) =>
  handleResponse(() => API.get(`/reminders/reminders/parent/${parentId}`));
export const updateReminderStatusByParent = async (statusData) =>
  handleResponse(() => API.put('/reminders/assignments/status', statusData));
export const fetchAdminReminders = async () =>
  handleResponse(() => API.get('/reminders/reminders/admin'));
export const fetchParents = async () =>
  handleResponse(() => API.get('/users/parents'));
export const createReminder = async (reminderData) =>
  handleResponse(() => API.post('/reminders/reminders', reminderData));
export const updateReminder = async (reminderId, reminderData) =>
  handleResponse(() =>
    API.put(`/reminders/reminders/${reminderId}`, reminderData)
  );
export const deleteReminder = async (reminderId) =>
  handleResponse(() => API.delete(`/reminders/reminders/${reminderId}`));
export const fetchCohorts = async () =>
  handleResponse(() => API.get('/cohorts'));
export const fetchSessionsWithParticipants = async (activityId) => {
  try {
    const response = await API.get(
      `/sessions/${activityId}/sessions-with-participants`
    );
    if (response && Array.isArray(response.data)) {
      return response.data; // Directly return the array
    } else {
      console.error('Invalid response structure:', response.data);
      return [];
    }
  } catch (error) {
    console.error('Error fetching sessions with participants:', error);
    throw error;
  }
};
export const cancelInfoRequest = async (requestId) => {
  try {
    const response = await API.post(`/attendance/cancel-info-request/${requestId}`);
    return response.data;
  } catch (error) {
    console.error('Error cancelling info request:', error);
    throw error;
  }
};
export const fetchNextSessionWithParticipants = async (activityId) =>
  handleResponse(() =>
    API.get(`/sessions/${activityId}/next-session-with-participants`)
  );
export const fetchAllPendingApprovals = async () =>
    handleResponse(() => API.get('/absence-approvals/pending/all'));
export const rescheduleSession = async (
  sessionId,
  newDate,
  newStartTime,
  newEndTime
) =>
  handleResponse(() =>
    API.patch(`/sessions/reschedule/${sessionId}`, {
      newDate,
      newStartTime,
      newEndTime,
    })
  );

export const createOrUpdateActivity = async (
  activityData,
  isEditing,
  activityId = null
) => {
  const apiCall = isEditing
    ? () => API.put(`/activities/${activityId}`, activityData)
    : () => API.post('/activities', activityData);
  return handleResponse(apiCall);
};

export const saveSessionNote = async (noteData) =>
  handleResponse(() => API.post('/session-notes/notes', noteData));

export const deleteSessionNote = async (noteId) => {
  if (!noteId) {
    throw new Error('Invalid noteId');
  }
  return handleResponse(() => API.delete(`/session-notes/notes/${noteId}`));
};

export const cancelSession = async (sessionId, cancellationReason = '') => {
  console.log(
    'Cancel session called with sessionId:',
    sessionId,
    'Reason:',
    cancellationReason
  );
  return handleResponse(() =>
    API.patch(`/sessions/cancel/${sessionId}`, { cancellationReason })
  );
};

export const uncancelSession = async (sessionId) => {
  console.log('Uncancel session called with sessionId:', sessionId);
  return handleResponse(() => API.patch(`/sessions/uncancel/${sessionId}`));
};

export const fetchNotesBySessionId = async (sessionId) =>
  handleResponse(() => API.get(`/session-notes/notes/${sessionId}`));

export const uploadFiles = async (activityId, formData) => {
  const response = await API.post(
    `/activities/${activityId}/upload`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
};

export const deleteFileFromActivity = async (activityId, fileId) => {
  const response = await API.delete(
    `/activities/${activityId}/files/${fileId}`
  );
  return response.data;
};

export const downloadFile = (filePath) => {
  const baseURL = process.env.VUE_APP_API_BASE_URL || 'http://localhost:5005';
  const trimmedBaseURL = baseURL.replace(/\/+$/, '');
  let correctedFilePath = filePath;

  const baseURLWithoutAPI = trimmedBaseURL.replace(/\/api$/, '');

  if (!filePath.startsWith('/uploads')) {
    correctedFilePath = `/uploads/${filePath.split('/').pop()}`;
  }

  const fullURL = `${baseURLWithoutAPI}${correctedFilePath}`;
  const link = document.createElement('a');
  link.href = fullURL;
  link.download = correctedFilePath.split('/').pop();
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const fetchRolePermissions = async (roleId) => {
  try {
    const response = await API.get(`/permissions/${roleId}/permissions`);
    return response.data;
  } catch (error) {
    console.error('Error fetching role permissions:', error);
    throw new Error(
      'Error fetching role permissions: ' + error.response.data.message
    );
  }
};

export const fetchCombinedHolidays = async () => {
  try {
    const { data } = await API.get('/combined/combined-holidays');

    const publicHolidays = data.publicHolidays.map((holiday) => ({
      title: holiday.localName,
      start: holiday.date,
      allDay: true,
      classNames: ['public-holiday-event'],
    }));

    const schoolHolidays = data.schoolHolidays.map((holiday) => ({
      title: holiday.name,
      start: holiday.startDate,
      end: holiday.endDate,
      allDay: true,
      classNames: ['school-holiday-event'],
    }));

    return [...publicHolidays, ...schoolHolidays];
  } catch (error) {
    console.error('Error fetching combined holidays:', error);
    return [];
  }
};

export const formatActivitiesToEvents = (activities, selectedActivities) => {
  const dayOfWeekMap = {
    Sunday: 0,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
  };

  const events = [];

  activities.forEach((activityWrapper) => {
    if (!selectedActivities.includes(activityWrapper.activity._id)) return;

    const activity = activityWrapper.activity;
    const bookingStatus = activityWrapper.bookingStatus;

    if (activity.activityType === 'slotbooking') {
      activity.timeSlots.forEach((slot) => {
        const eventDate = new Date(slot.date);

        const startDateTime = new Date(eventDate);
        startDateTime.setUTCHours(...slot.startTime.split(':'));
        const endDateTime = new Date(eventDate);
        endDateTime.setUTCHours(...slot.endTime.split(':'));

        const className = slot.bookedOut ? 'event-booked' : 'event-available';

        events.push({
          title: activity.name,
          start: startDateTime.toISOString(),
          end: endDateTime.toISOString(),
          allDay: false,
          classNames: [className],
          extendedProps: {
            activityWrapper,
            slotId: slot.slotId,
            sessionId: slot.sessionId,
            bookedOut: slot.bookedOut,
          },
        });
      });
    } else {
      const startDate = new Date(activity.startDate);
      const endDate = new Date(activity.endDate);

      for (
        let currentDate = new Date(startDate);
        currentDate <= endDate;
        currentDate.setDate(currentDate.getDate() + 1)
      ) {
        const currentDay = currentDate.getUTCDay();

        activity.timeSlots.forEach((slot) => {
          if (currentDay === dayOfWeekMap[slot.dayOfWeek]) {
            const startDateTime = new Date(currentDate);
            startDateTime.setUTCHours(...slot.startTime.split(':'));
            const endDateTime = new Date(currentDate);
            endDateTime.setUTCHours(...slot.endTime.split(':'));

            const className = `event-${bookingStatus.replace(' ', '-')}`;

            events.push({
              title: activity.name,
              start: startDateTime.toISOString(),
              end: endDateTime.toISOString(),
              allDay: false,
              classNames: [className],
              extendedProps: {
                activityWrapper,
                sessionId: slot._id,
              },
            });
          }
        });
      }
    }
  });

  return events;
};

export const confirmBooking = async (
  activity,
  selectedChildName,
  slotDetails = null
) => {
  try {
    const bookingInfo = {
      activityId: activity._id,
      childName: selectedChildName,
      slotDetails: slotDetails
        ? { slotId: slotDetails.slotId, sessionId: slotDetails.sessionId }
        : null,
    };
    await API.post('/booking/bookActivity', bookingInfo);
    console.log('Booking confirmed for', activity.name);
  } catch (error) {
    console.error('Error confirming booking:', error);
    throw new Error('Failed to confirm booking.');
  }
};
export const fetchApprovalFlows = async () => {
  try {
    const response = await fetch('/api/absence-approvals/flows', {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch approval flows');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching approval flows:', error);
    throw error;
  }
};

// Create a new approval flow
export const createApprovalFlow = async (flowData) => {
  try {
    const response = await fetch('/api/absence-approvals/flows', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(flowData)
    });
    
    if (!response.ok) {
      throw new Error('Failed to create approval flow');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error creating approval flow:', error);
    throw error;
  }
};

// Update an existing approval flow
export const updateApprovalFlow = async (flowId, flowData) => {
  try {
    const response = await fetch(`/api/absence-approvals/flows/${flowId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(flowData)
    });
    
    if (!response.ok) {
      throw new Error('Failed to update approval flow');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error updating approval flow:', error);
    throw error;
  }
};

// Delete an approval flow
export const deleteApprovalFlow = async (flowId) => {
  try {
    const response = await fetch(`/api/absence-approvals/flows/${flowId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to delete approval flow');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error deleting approval flow:', error);
    throw error;
  }
};

// Set a flow as the default
export const setDefaultFlow = async (flowId) => {
  try {
    const response = await fetch(`/api/absence-approvals/flows/${flowId}/default`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to set default approval flow');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error setting default approval flow:', error);
    throw error;
  }
};

// Activate a flow
export const activateFlow = async (flowId) => {
  try {
    const response = await fetch(`/api/absence-approvals/flows/${flowId}/activate`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to activate approval flow');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error activating approval flow:', error);
    throw error;
  }
};

// Deactivate a flow
export const deactivateFlow = async (flowId) => {
  try {
    const response = await fetch(`/api/absence-approvals/flows/${flowId}/deactivate`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to deactivate approval flow');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error deactivating approval flow:', error);
    throw error;
  }
};

// Get pending approval requests
export const fetchPendingApprovals = async () => {
  try {
    const response = await fetch('/api/absence-approvals/pending', {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch pending approvals');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching pending approvals:', error);
    throw error;
  }
};

// Approve a request
export const approveAbsenceRequest = async (requestId, comment) => {
  try {
    const response = await fetch(`/api/absence-approvals/${requestId}/approve`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({ comment })
    });
    
    if (!response.ok) {
      throw new Error('Failed to approve request');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error approving request:', error);
    throw error;
  }
};

// Reject a request
export const rejectAbsenceRequest = async (requestId, comment) => {
  try {
    const response = await fetch(`/api/absence-approvals/${requestId}/reject`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({ comment })
    });
    
    if (!response.ok) {
      throw new Error('Failed to reject request');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error rejecting request:', error);
    throw error;
  }
};

// Get approval history for a request
export const getApprovalHistory = async (requestId) => {
  try {
    const response = await fetch(`/api/absence-approvals/history/${requestId}`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch approval history');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching approval history:', error);
    throw error;
  }
};

// Payment and Invoice API functions for the Payment Hub
// Add these to your existing apiService.js file

// PAYMENT APIs

/**
 * Fetches payments associated with a specific booking
 * @param {string} bookingId - The ID of the booking
 * @returns {Promise<Array>} Array of payment objects
 */
export const fetchPaymentsByBooking = async (bookingId) => {
  return handleResponse(() => API.get(`/payments/booking/${bookingId}`));
};

/**
 * Fetches payments associated with a specific parent
 * @param {string} parentId - The ID of the parent
 * @returns {Promise<Array>} Array of payment objects
 */
export const fetchPaymentsByParent = async (parentId) => {
  return handleResponse(() => API.get(`/payments/parent/${parentId}`));
};

/**
 * Records a manual payment for a booking
 * @param {Object} paymentData - Payment information
 * @returns {Promise<Object>} Created payment object
 */
export const recordPayment = async (paymentData) => {
  return handleResponse(() => API.post('/payments/manual', paymentData));
};

/**
 * Updates payment details
 * @param {string} paymentId - The ID of the payment to update
 * @param {Object} paymentData - Updated payment information
 * @returns {Promise<Object>} Updated payment object
 */
export const updatePaymentDetails = async (paymentId, paymentData) => {
  return handleResponse(() => API.put(`/payments/${paymentId}`, paymentData));
};

/**
 * Marks a payment as paid
 * @param {string} paymentId - The ID of the payment to mark as paid
 * @param {Object} paymentData - Payment transaction data
 * @returns {Promise<Object>} Updated payment object
 */
export const markPaymentAsPaid = async (paymentId, paymentData) => {
  return handleResponse(() => API.post(`/payments/${paymentId}/make`, paymentData));
};

/**
 * Cancels a pending payment
 * @param {string} paymentId - The ID of the payment to cancel
 * @returns {Promise<Object>} Updated payment object
 */
export const cancelPaymentRequest = async (paymentId) => {
  return handleResponse(() => API.post(`/payments/${paymentId}/cancel`));
};

/**
 * Creates and processes a payment for an invoice
 * @param {string} invoiceId - The ID of the invoice to pay
 * @param {Object} paymentData - Payment information
 * @returns {Promise<Object>} Created payment and updated invoice
 */
export const makeInvoicePayment = async (invoiceId, paymentData) => {
  return handleResponse(() => API.post(`/payments/invoice/${invoiceId}/pay`, paymentData));
};

// REFUND APIs

/**
 * Creates a new refund request
 * @param {Object} refundData - Refund information
 * @returns {Promise<Object>} Created refund object
 */
export const createRefund = async (refundData) => {
  return handleResponse(() => API.post('/refunds', refundData));
};

/**
 * Approves a refund request
 * @param {string} refundId - The ID of the refund to approve
 * @param {Object} approvalData - Approval information
 * @returns {Promise<Object>} Updated refund object
 */
export const approveRefund = async (refundId, approvalData) => {
  return handleResponse(() => API.post(`/refunds/${refundId}/approve`, approvalData));
};

/**
 * Processes a refund
 * @param {string} refundId - The ID of the refund to process
 * @returns {Promise<Object>} Updated refund object
 */
export const processRefund = async (refundId) => {
  return handleResponse(() => API.post(`/refunds/${refundId}/process`));
};

/**
 * Cancels a refund
 * @param {string} refundId - The ID of the refund to cancel
 * @param {Object} cancelData - Cancellation information
 * @returns {Promise<Object>} Updated refund object
 */
export const cancelRefund = async (refundId, cancelData) => {
  return handleResponse(() => API.post(`/refunds/${refundId}/cancel`, cancelData));
};

/**
 * Gets refund by ID
 * @param {string} refundId - The ID of the refund
 * @returns {Promise<Object>} Refund object
 */
export const getRefundById = async (refundId) => {
  return handleResponse(() => API.get(`/refunds/${refundId}`));
};

/**
 * Gets refunds by parent
 * @param {string} parentId - The ID of the parent
 * @returns {Promise<Array>} Array of refund objects
 */
export const getRefundsByParent = async (parentId) => {
  return handleResponse(() => API.get(`/refunds/parent/${parentId}`));
};

/**
 * Gets refunds by booking
 * @param {string} bookingId - The ID of the booking
 * @returns {Promise<Array>} Array of refund objects
 */
export const getRefundsByBooking = async (bookingId) => {
  return handleResponse(() => API.get(`/refunds/booking/${bookingId}`));
};

/**
 * Gets pending refund approvals
 * @returns {Promise<Array>} Array of pending refund objects
 */
export const getPendingRefundApprovals = async () => {
  return handleResponse(() => API.get('/refunds/pending/approvals'));
};

/**
 * Applies a credit note to an invoice
 * @param {Object} creditNoteData - Credit note application data
 * @returns {Promise<Object>} Result of credit note application
 */
export const applyCreditNote = async (creditNoteData) => {
  return handleResponse(() => API.post('/refunds/credit-note/apply', creditNoteData));
};

/**
 * Gets refund statistics
 * @param {Object} params - Query parameters for date range
 * @returns {Promise<Object>} Refund statistics
 */
export const getRefundStatistics = async (params = {}) => {
  return handleResponse(() => API.get('/refunds/statistics', { params }));
};

/**
 * Gets available payments for refund
 * @param {Object} params - Query parameters (parentId or bookingId)
 * @returns {Promise<Array>} Array of available payment objects
 */
export const getAvailablePaymentsForRefund = async (params = {}) => {
  return handleResponse(() => API.get('/refunds/available-payments', { params }));
};

/**
 * Gets refund history for a payment
 * @param {string} paymentId - The ID of the payment
 * @returns {Promise<Array>} Array of refund objects
 */
export const getRefundHistory = async (paymentId) => {
  return handleResponse(() => API.get(`/refunds/payment/${paymentId}/history`));
};

/**
 * Fetches all pending payments
 * @returns {Promise<Array>} Array of pending payment objects
 */
export const getPendingPayments = async () => {
  return handleResponse(() => API.get('/payments/pending'));
};

/**
 * Fetches all overdue payments
 * @returns {Promise<Array>} Array of overdue payment objects
 */
export const getOverduePayments = async () => {
  return handleResponse(() => API.get('/payments/overdue'));
};

// INVOICE APIs

/**
 * Fetches a specific invoice by ID
 * @param {string} invoiceId - The ID of the invoice
 * @returns {Promise<Object>} Invoice object
 */
export const getInvoice = async (invoiceId) => {
  return handleResponse(() => API.get(`/invoices/${invoiceId}`));
};

/**
 * Fetches invoices associated with a specific booking
 * @param {string} bookingId - The ID of the booking
 * @returns {Promise<Array>} Array of invoice objects
 */
export const fetchInvoicesByBooking = async (bookingId) =>
  handleResponse(() => API.get(`/invoices/booking/${bookingId}`));

/**
 * Creates a new invoice
 * @param {Object} invoiceData - Invoice information
 * @returns {Promise<Object>} Created invoice object
 */
export const createInvoice = async (invoiceData) => {
  return handleResponse(() => API.post('/invoices', invoiceData));
};

/**
 * Updates an existing invoice
 * @param {string} invoiceId - The ID of the invoice to update
 * @param {Object} invoiceData - Updated invoice information
 * @returns {Promise<Object>} Updated invoice object
 */
export const updateInvoice = async (invoiceId, invoiceData) => {
  return handleResponse(() => API.put(`/invoices/${invoiceId}`, invoiceData));
};

/**
 * Updates the status of an invoice
 * @param {string} invoiceId - The ID of the invoice
 * @param {Object} statusData - Status update information
 * @returns {Promise<Object>} Updated invoice object
 */
export const updateInvoiceStatus = async (invoiceId, statusData) => {
  return handleResponse(() => API.patch(`/invoices/${invoiceId}/status`, statusData));
};

/**
 * Issues an invoice (changes status from draft to issued)
 * @param {string} invoiceId - The ID of the invoice to issue
 * @returns {Promise<Object>} Updated invoice object
 */
export const issueInvoice = async (invoiceId) => {
  return handleResponse(() => API.post(`/invoices/${invoiceId}/issue`));
};

/**
 * Voids an invoice
 * @param {string} invoiceId - The ID of the invoice to void
 * @returns {Promise<Object>} Updated invoice object
 */
export const voidInvoiceRequest = async (invoiceId) => {
  return handleResponse(() => API.post(`/invoices/${invoiceId}/void`));
};

/**
 * Fetches invoices associated with a specific parent
 * @param {string} parentId - The ID of the parent
 * @returns {Promise<Array>} Array of invoice objects
 */
export const fetchInvoicesByParent = async (parentId) =>
  handleResponse(() => API.get(`/invoices/parent/${parentId}`));

/**
 * Fetches unpaid invoices
 * @returns {Promise<Array>} Array of unpaid invoice objects
 */
export const getUnpaidInvoices = async () => {
  return handleResponse(() => API.get('/invoices/unpaid'));
};

/**
 * Fetches overdue invoices
 * @returns {Promise<Array>} Array of overdue invoice objects
 */
export const getOverdueInvoices = async () => {
  return handleResponse(() => API.get('/invoices/overdue'));
};

/**
 * Generates a new invoice number
 * @returns {Promise<Object>} Object containing generated invoice number
 */
export const generateInvoiceNumber = async () => {
  return handleResponse(() => API.get('/invoices/generate-number'));
};

/**
 * Generates an invoice for a booking
 * @param {string} bookingId - The ID of the booking
 * @returns {Promise<Object>} Created invoice object
 */
export const generateBookingInvoice = async (bookingId) => {
  return handleResponse(() => API.post(`/invoices/booking/${bookingId}/generate`));
};

/**
 * Generates a multi-item invoice for child/parent payment hubs
 * @param {Object} invoiceData - Invoice data including entityType, entityId, items, etc.
 * @returns {Promise<Object>} Created invoice object
 */
export const generateMultiItemInvoice = async (invoiceData) => {
  return handleResponse(() => API.post('/invoices/multi-item/generate', invoiceData));
};

/**
 * Sends invoice via email
 * @param {string} invoiceId - The ID of the invoice
 * @returns {Promise<Object>} Result of the email sending operation
 */
export const sendInvoiceEmail = async (invoiceId) => {
  return handleResponse(() => API.post(`/invoices/${invoiceId}/send`));
};

/**
 * Fetches a specific booking by ID
 * @param {string} bookingId - The ID of the booking
 * @returns {Promise<Object>} Booking object
 */
export const fetchBookingById = async (bookingId) => {
  return handleResponse(() => API.get(`payments/booking/${bookingId}`));
};

/**
 * Fetches detailed information for a specific invoice
 * @param {string} invoiceId - The ID of the invoice to fetch
 * @returns {Promise<Object>} - Promise resolving to the invoice details
 */
export const fetchInvoiceDetails = async (invoiceId) => {
  return handleResponse(() => API.get(`/invoices/${invoiceId}`));
};

export const downloadInvoicePdf = async (invoiceId) => {
  try {
    // Make sure you're using axios or your existing HTTP client
    // If you don't have apiClient defined in this file, you can use axios directly
    const response = await axios.get(`/invoices/${invoiceId}/download`, {
      responseType: 'blob' // Important: This tells axios to handle the response as a binary blob
    });
    
    // Create a URL for the blob
    const url = window.URL.createObjectURL(new Blob([response.data]));
    
    // Get invoice number from Content-Disposition header or use a default
    let filename = 'invoice.pdf';
    const contentDisposition = response.headers['content-disposition'];
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch && filenameMatch.length === 2) {
        filename = filenameMatch[1];
      }
    }
    
    // Create a temporary link element to trigger the download
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    window.URL.revokeObjectURL(url);
    document.body.removeChild(link);
    
    Notify.create({
      type: 'positive',
      message: 'Invoice PDF download started',
      position: 'top-right',
      timeout: 2000
    });
    
    return true;
  } catch (error) {
    console.error('Error downloading invoice PDF:', error);
    
    Notify.create({
      type: 'negative',
      message: 'Could not download the invoice PDF',
      position: 'top-right',
      timeout: 3000
    });
    
    throw error;
  }
}

export const fetchUnpaidCostsByParent = async () => {
  const response = await axios.get('/booking/unpaid-costs-by-parent');
  return response.data;
};

export const fetchUnpaidCostsByChild = async () => {
  const response = await axios.get('/booking/unpaid-costs-by-child');
  return response.data;
};

export const fetchUnpaidDetailsByParent = async (parentId) => {
  const response = await axios.get(`/booking/unpaid-details-by-parent/${parentId}`);
  return response.data;
};

export const fetchUnpaidDetailsByChild = async (childId) => {
  const response = await axios.get(`/booking/unpaid-details-by-child/${childId}`);
  return response.data;
};

export const fetchUnpaidItemsByChild = async (childId) =>
  handleResponse(() => API.get(`/payments/unpaid/child/${childId}`));

export const fetchUnpaidItemsByParent = async (parentId) =>
  handleResponse(() => API.get(`/payments/unpaid/parent/${parentId}`));

export const fetchInvoicesByChild = async (childId) => {
  // For child context, we need to get the parent first, then fetch parent's invoices
  try {
    const childResponse = await API.get(`/users/${childId}`);
    const child = childResponse.data;
    if (child && child.parent && child.parent.length > 0) {
      const parentId = child.parent[0];
      return await handleResponse(() => API.get(`/invoices/parent/${parentId}`));
    }
    return [];
  } catch (error) {
    console.error('Error fetching invoices by child:', error);
    return [];
  }
};

export const exportPayments = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams(filters).toString();
    const response = await API.get(`/export/payments?${queryParams}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error('Error exporting payments:', error);
    throw error;
  }
};

export const exportInvoices = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams(filters).toString();
    const response = await API.get(`/export/invoices?${queryParams}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error('Error exporting invoices:', error);
    throw error;
  }
};

export const exportUnpaidItems = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams(filters).toString();
    const response = await API.get(`/export/unpaid-items?${queryParams}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error('Error exporting unpaid items:', error);
    throw error;
  }
};

export const exportFinancialSummary = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams(filters).toString();
    const response = await API.get(`/export/financial-summary?${queryParams}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error('Error exporting financial summary:', error);
    throw error;
  }
};

export const exportAllFinancialData = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams(filters).toString();
    const response = await API.get(`/export/all-financial-data?${queryParams}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error('Error exporting all financial data:', error);
    throw error;
  }
};

// Analytics API functions
export const fetchFinancialSummary = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const response = await API.get(`/analytics/financial-summary?${queryString}`);
  return response.data;
};

export const fetchTopDebtors = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const response = await API.get(`/analytics/top-debtors?${queryString}`);
  return response.data;
};

export const fetchPaymentMethodsBreakdown = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const response = await API.get(`/analytics/payment-methods?${queryString}`);
  return response.data;
};

export const fetchActivityRevenue = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const response = await API.get(`/analytics/activity-revenue?${queryString}`);
  return response.data;
};

// Bank Import Wizard API
export const uploadBankFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await API.post('/bank-import/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return res.data;
};

export const previewBankFile = async (fileId) => {
  const res = await API.post('/bank-import/preview', { fileId });
  return res.data;
};

export const saveMappingTemplate = async (mapping) => {
  const res = await API.post('/bank-import/save-mapping', mapping);
  return res.data;
};

export const listMappingTemplates = async () => {
  const res = await API.get('/bank-import/mappings');
  return res.data;
};

export const matchPaymentsToInvoices = async (fileId, mapping) => {
  const res = await API.post('/bank-import/match', { fileId, mapping });
  return res.data;
};

export const confirmImport = async (importId, matches) => {
  const res = await API.post('/bank-import/confirm', { importId, matches });
  return res.data;
};

export const importHistory = async () => {
  const res = await API.get('/bank-import/history');
  return res.data;
};

export const matchPayments = async ({ fileId, mapping }) => {
  return matchPaymentsToInvoices(fileId, mapping);
};

export default API;
export { API };
